import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { captureEvent } from '../../../../../utils/posthog'
import { POSTHOG_EVENTS } from '../../../../../constants/posthog'
import { Card, Header, Title, DiscountBadge, Row, InnerRow, Label, Value, OldValue, Link, Placeholder } from './styles'
import CalculationModal from './components/CalculationModal'

const currencyFormatter = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL'
})

const buildDiscountMessage = percentage => {
  return percentage ? `Você ganhou ${percentage.toFixed(2).replace('.', ',')}% de desconto` : 'Você ganhou desconto'
}

const DefaultDiscountIcon = () => (
  <svg width="40" height="40" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <g clipPath="url(#clip0_6810_8648)">
      <path
        d="M9.08329 1.75L17.3325 2.92917L18.5108 11.1792L10.8508 18.8392C10.6945 18.9954 10.4826 19.0832 10.2616 19.0832C10.0407 19.0832 9.82873 18.9954 9.67246 18.8392L1.42246 10.5892C1.26623 10.4329 1.17847 10.221 1.17847 10C1.17847 9.77903 1.26623 9.56711 1.42246 9.41083L9.08329 1.75ZM11.44 8.82167C11.5948 8.97641 11.7785 9.09914 11.9807 9.18287C12.183 9.26659 12.3997 9.30967 12.6186 9.30963C12.8375 9.30959 13.0542 9.26644 13.2564 9.18264C13.4586 9.09885 13.6423 8.97605 13.797 8.82125C13.9518 8.66645 14.0745 8.4827 14.1582 8.28047C14.242 8.07824 14.285 7.8615 14.285 7.64262C14.285 7.42375 14.2418 7.20702 14.158 7.00482C14.0742 6.80262 13.9514 6.61891 13.7966 6.46417C13.6418 6.30943 13.4581 6.18669 13.2558 6.10296C13.0536 6.01924 12.8369 5.97617 12.618 5.97621C12.176 5.97629 11.7521 6.15196 11.4395 6.46458C11.127 6.77721 10.9515 7.20117 10.9516 7.64321C10.9517 8.08525 11.1273 8.50915 11.44 8.82167Z"
        fill="white"
      />
    </g>
    <defs>
      <clipPath id="clip0_6810_8648">
        <rect width="20" height="20" fill="white" />
      </clipPath>
    </defs>
  </svg>
)

const formatCurrency = value => {
  if (value === null || value === undefined || value === '') {
    return '-'
  }
  // Se já vier formatado como string (ex: "R$ 2.556,74"), retorna direto
  if (typeof value === 'string' && value.includes('R$')) {
    return value
  }
  if (typeof value === 'number') {
    return currencyFormatter.format(value)
  }
  return value
}

const formatDateValue = value => {
  if (!value) {
    return '-'
  }
  // Se já vier formatado como string (ex: "13/09/2025"), retorna direto
  if (typeof value === 'string' && /^\d{2}\/\d{2}\/\d{4}$/.test(value)) {
    return value
  }
  const parts = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (parts) {
    const [, year, month, day] = parts
    return `${day}/${month}/${year}`
  }
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) {
    return value
  }
  return new Intl.DateTimeFormat('pt-BR').format(parsed)
}

const formatInstallments = ({ installments, installmentValue }) => {
  if (!installments || installmentValue === undefined || installmentValue === null) {
    return '-'
  }
  return `${installments}x de ${formatCurrency(installmentValue)}`
}

const buildPaymentValue = entry => {
  if (!entry) {
    return '-'
  }
  if (entry.type === 'installments') {
    return formatInstallments(entry)
  }
  if (entry.type === 'currency') {
    return formatCurrency(entry.value)
  }
  // Para tipo 'text', retorna o valor direto (já formatado)
  return entry.value ?? '-'
}

const filterCalculationForStorage = calc => {
  if (!calc) return null
  // Manter TODOS os dados do cálculo para preservar informações na tela de assinatura
  // Isso garante que o modal de cálculo tenha as mesmas informações da tela de simulação
  return {
    title: calc.title,
    description: calc.description,
    totals: calc.totals
      ? {
          overdue: calc.totals.overdue,
          future: calc.totals.future,
          total: calc.totals.total
        }
      : null,
    installments: calc.installments?.map(installment => ({
      id: installment.id,
      label: installment.label,
      status: installment.status,
      amount: installment.amount,
      details: installment.details
        ? {
            dueDate: installment.details.dueDate,
            daysOverdue: installment.details.daysOverdue,
            daysLate: installment.details.daysLate,
            originalValue: installment.details.originalValue,
            remuneratoryInterest: installment.details.remuneratoryInterest,
            remuneratoryRate: installment.details.remuneratoryRate,
            defaultInterest: installment.details.defaultInterest,
            fine: installment.details.fine,
            presentValueDiscount: installment.details?.presentValueDiscount
          }
        : null
    }))
  }
}

export const SummaryCard = ({
  title = 'Resumo do acordo',
  discount,
  outstanding,
  paymentPlan,
  schedule,
  calculation,
  isLoading = false,
  hasError = false,
  onViewCalculation,
  variant = 'default',
  showDiscount = true,
  cashPayment,
  signatureStatus,
  screen
}) => {
  const [isCalculationModalOpen, setIsCalculationModalOpen] = useState(false)

  // Persist summary info to localStorage on every render
  useEffect(() => {
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        const payload = {
          title,
          variant,
          showDiscount,
          cashPayment,
          isLoading,
          hasError,
          discount: discount ? { percentage: discount.percentage, message: discount.message } : null,
          outstanding,
          paymentPlan,
          schedule,
          calculation: filterCalculationForStorage(calculation),
          signatureStatus
        }
        localStorage.setItem('sumary_info', JSON.stringify(payload))
      }
    } catch (e) {
      // Silently fail if localStorage is unavailable
    }
  }, [
    title,
    variant,
    showDiscount,
    cashPayment,
    isLoading,
    hasError,
    discount,
    outstanding,
    paymentPlan,
    schedule,
    calculation,
    signatureStatus
  ])

  const handleCalculationClick = () => {
    captureEvent(POSTHOG_EVENTS.VIEW, {
      tela: 'renegociacao_detalhes_calculo',
      descricao: 'Visualizou os detalhes do cálculo durante uma simulação',
    })

    if (calculation) {
      setIsCalculationModalOpen(true)
      return
    }
    if (onViewCalculation) {
      onViewCalculation()
    }
  }

  // Resolve discount com fallback
  const resolvedDiscount = {
    percentage: discount?.percentage,
    message: discount?.message ?? buildDiscountMessage(discount?.percentage),
    icon: discount?.icon
  }

  // Resolve outstanding com fallback para valores vazios
  const resolvedOutstanding = {
    label: outstanding?.label || 'Saldo devedor',
    currentValue: outstanding?.currentValue,
    previousValue: outstanding?.previousValue,
    linkLabel: outstanding?.linkLabel || 'Ver cálculo',
    linkHref: outstanding?.linkHref,
    linkTarget: outstanding?.linkTarget,
    linkRel: outstanding?.linkRel
  }

  // Payment plan e schedule usam os valores passados diretamente
  const resolvedPaymentPlan = paymentPlan?.length ? paymentPlan : []
  const resolvedSchedule = schedule?.length ? schedule : []

  const hasOutstandingValue = resolvedOutstanding.currentValue !== undefined && resolvedOutstanding.currentValue !== null

  const sections = [
    {
      id: 'outstanding',
      rows: hasOutstandingValue
        ? [
            {
              key: 'outstanding',
              label: resolvedOutstanding.label,
              labelPlaceholderWidth: '80%',
              labelProps: { dark: true },
              linkLabel: resolvedOutstanding.linkLabel,
              linkHref: resolvedOutstanding.linkHref,
              linkTarget: resolvedOutstanding.linkTarget,
              linkRel: resolvedOutstanding.linkRel,
              value: formatCurrency(resolvedOutstanding.currentValue),
              valuePlaceholderWidth: '60%',
              valueProps: { dark: true },
              secondaryValue:
                resolvedOutstanding.previousValue !== undefined && resolvedOutstanding.previousValue !== null
                  ? formatCurrency(resolvedOutstanding.previousValue)
                  : null,
              alignTop: Boolean(resolvedOutstanding.previousValue)
            }
          ]
        : []
    },
    {
      id: 'payment-plan',
      rows: resolvedPaymentPlan.map(item => ({
        key: item.id || item.label,
        label: item.label,
        labelPlaceholderWidth: '70%',
        labelProps: { dark: true },
        value: buildPaymentValue(item),
        valuePlaceholderWidth: '55%',
        valueProps: { dark: true, large: item.highlight }
      }))
    },
    {
      id: 'schedule',
      rows: resolvedSchedule.map(item => ({
        key: item.id || item.label,
        label: item.label,
        labelPlaceholderWidth: '60%',
        labelProps: { gray: true },
        value: formatDateValue(item.date),
        valuePlaceholderWidth: '45%',
        valueProps: { gray: true }
      }))
    }
  ].filter(section => section.rows.length)

  const renderContent = (content, placeholderWidth) =>
    isLoading ? <Placeholder $width={placeholderWidth} aria-hidden="true" /> : content

  if (hasError) {
    return (
      <Card>
        <Header>
          <Title>{title}</Title>
        </Header>
        <Row>
          <InnerRow>
            <Label dark>Não foi possível carregar o resumo</Label>
            <Value dark>Atualize ou tente novamente mais tarde.</Value>
          </InnerRow>
        </Row>
      </Card>
    )
  }

  const shouldShowDiscount = showDiscount

  // Render special layout for cash payment
  if (cashPayment) {
    return (
      <>
        <Card $variant="cash">
          <Header>
            <Title $variant="cash">{title}</Title>
          </Header>

          {shouldShowDiscount && (
            <DiscountBadge $variant="cash">
              {resolvedDiscount.icon || <DefaultDiscountIcon />}
              {renderContent(resolvedDiscount.message, '80%')}
            </DiscountBadge>
          )}

          <Row $variant="cash" $isFirst $bordered>
            <InnerRow alignTop>
              <Label dark $cashVariant>
                {renderContent(resolvedOutstanding.label, '80%')}
                {!isLoading &&
                  resolvedOutstanding.linkLabel &&
                  (calculation ? (
                    <Link as="button" type="button" onClick={handleCalculationClick}>
                      {resolvedOutstanding.linkLabel}
                    </Link>
                  ) : (
                    <Link as="button" type="button" onClick={onViewCalculation} $disabled={!onViewCalculation}>
                      {resolvedOutstanding.linkLabel}
                    </Link>
                  ))}
              </Label>
              <Value dark $cashVariant>
                {renderContent(formatCurrency(resolvedOutstanding.currentValue), '60%')}
                {!isLoading && resolvedOutstanding.previousValue && (
                  <OldValue $cashVariant>{formatCurrency(resolvedOutstanding.previousValue)}</OldValue>
                )}
              </Value>
            </InnerRow>
          </Row>

          {resolvedPaymentPlan.map((item, index) => (
            <Row key={item.id || item.label} $variant="cash" $bordered={index === 0}>
              <InnerRow>
                <Label dark $cashVariant>
                  {renderContent(item.label, '70%')}
                </Label>
                <Value dark $cashVariant large={item.highlight}>
                  {renderContent(buildPaymentValue(item), '55%')}
                </Value>
              </InnerRow>
            </Row>
          ))}

          {resolvedSchedule.map(item => (
            <Row key={item.id || item.label} $variant="cash" $isLast>
              <InnerRow>
                <Label gray $cashVariant>
                  {renderContent(item.label, '60%')}
                </Label>
                <Value gray $cashVariant>
                  {renderContent(formatDateValue(item.date), '45%')}
                </Value>
              </InnerRow>
            </Row>
          ))}
        </Card>
        {calculation && (
          <CalculationModal
            isOpen={isCalculationModalOpen}
            onClose={() => setIsCalculationModalOpen(false)}
            calculation={calculation}
          />
        )}
      </>
    )
  }

  return (
    <>
      <Card $variant={variant}>
        <Header>
          <Title $variant={variant}>{title}</Title>
        </Header>

        {shouldShowDiscount && (
          <DiscountBadge $variant={variant}>
            {resolvedDiscount.icon || <DefaultDiscountIcon />}
            {renderContent(resolvedDiscount.message, '80%')}
          </DiscountBadge>
        )}

        {sections.map((section, index) => (
          <Row
            key={section.id}
            $variant={variant}
            $isFirst={index === 0 && !shouldShowDiscount}
            $isLast={index === sections.length - 1}
          >
            {section.rows.map(row => (
              <InnerRow key={row.key} alignTop={row.alignTop}>
                <Label {...row.labelProps}>
                  {renderContent(row.label, row.labelPlaceholderWidth)}
                  {!isLoading &&
                    row.linkLabel &&
                    (row.linkHref ? (
                      <Link
                        as="a"
                        href={row.linkHref}
                        target={row.linkTarget || '_blank'}
                        rel={row.linkRel || 'noopener noreferrer'}
                      >
                        {row.linkLabel}
                      </Link>
                    ) : calculation ? (
                      <Link as="button" type="button" onClick={handleCalculationClick}>
                        {row.linkLabel}
                      </Link>
                    ) : (
                      <Link as="button" type="button" onClick={onViewCalculation} $disabled={!onViewCalculation}>
                        {row.linkLabel}
                      </Link>
                    ))}
                </Label>
                <Value {...row.valueProps}>
                  {renderContent(row.value, row.valuePlaceholderWidth)}
                  {!isLoading && row.secondaryValue && <OldValue>{row.secondaryValue}</OldValue>}
                </Value>
              </InnerRow>
            ))}
          </Row>
        ))}
      </Card>
      {calculation && (
        <CalculationModal
          isOpen={isCalculationModalOpen}
          onClose={() => setIsCalculationModalOpen(false)}
          calculation={calculation}
        />
      )}
    </>
  )
}

SummaryCard.propTypes = {
  title: PropTypes.string,
  discount: PropTypes.shape({
    message: PropTypes.string,
    percentage: PropTypes.number,
    icon: PropTypes.node
  }),
  outstanding: PropTypes.shape({
    label: PropTypes.string,
    currentValue: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    previousValue: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    linkLabel: PropTypes.string,
    linkHref: PropTypes.string,
    linkTarget: PropTypes.string,
    linkRel: PropTypes.string
  }),
  paymentPlan: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      label: PropTypes.string.isRequired,
      type: PropTypes.oneOf(['currency', 'installments', 'text']),
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      installments: PropTypes.number,
      installmentValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      highlight: PropTypes.bool
    })
  ),
  schedule: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      label: PropTypes.string.isRequired,
      date: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)])
    })
  ),
  calculation: PropTypes.shape({
    title: PropTypes.string,
    description: PropTypes.string,
    totals: PropTypes.shape({
      overdue: PropTypes.number,
      future: PropTypes.number,
      total: PropTypes.number
    }),
    installments: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        label: PropTypes.string,
        status: PropTypes.string,
        amount: PropTypes.number,
        details: PropTypes.shape({
          dueDate: PropTypes.string,
          daysOverdue: PropTypes.number,
          originalValue: PropTypes.number,
          remuneratoryInterest: PropTypes.number,
          defaultInterest: PropTypes.number,
          fine: PropTypes.number
        })
      })
    )
  }),
  isLoading: PropTypes.bool,
  hasError: PropTypes.bool,
  onViewCalculation: PropTypes.func,
  variant: PropTypes.oneOf(['default', 'awaiting']),
  showDiscount: PropTypes.bool,
  cashPayment: PropTypes.bool,
  signatureStatus: PropTypes.shape({
    student: PropTypes.shape({
      status: PropTypes.string,
      statusText: PropTypes.string,
      name: PropTypes.string,
      cpf: PropTypes.string,
      email: PropTypes.string
    }),
    guarantor: PropTypes.shape({
      status: PropTypes.string,
      statusText: PropTypes.string,
      name: PropTypes.string,
      cpf: PropTypes.string,
      email: PropTypes.string
    })
  })
}
